import type { ReactNode, SVGProps } from "react";
import { cn } from "@/lib/utils";

/**
 * Custom line iconography for the /schools hero.
 * Hand-drawn geometry (24x24 grid, 1.5 stroke) so it does not look
 * like a stock icon library. Teal accents come from the design tokens.
 */

const base: SVGProps<SVGSVGElement> = {
  viewBox: "0 0 24 24",
  fill: "none",
  strokeWidth: 1.5,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": true,
  focusable: false,
};

/** Manage cohorts — connected people / group system */
export const CohortsIcon = ({ className }: { className?: string }) => (
  <svg {...base} className={cn("h-5 w-5", className)}>
    <g stroke="currentColor">
      <circle cx="12" cy="5.5" r="2.2" />
      <path d="M12 7.7v3.1M8.4 14.2 12 10.8l3.6 3.4" />
    </g>
    <g stroke="hsl(var(--accent))">
      <circle cx="5.6" cy="16.4" r="2.2" />
      <circle cx="18.4" cy="16.4" r="2.2" />
    </g>
    <path d="M4 21.2c.5-1.4 1.7-2.2 3.3-2.2M20 21.2c-.5-1.4-1.7-2.2-3.3-2.2" stroke="currentColor" opacity="0.65" />
  </svg>
);

/** Monitor student progress — node progression chart */
export const ProgressIcon = ({ className }: { className?: string }) => (
  <svg {...base} className={cn("h-5 w-5", className)}>
    <path d="M3.5 20h17" stroke="currentColor" opacity="0.55" />
    <path d="M4.5 16.2 9 12.4l3.4 2.4 6.1-6.5" stroke="currentColor" />
    <g stroke="hsl(var(--accent))" fill="hsl(var(--background))">
      <circle cx="9" cy="12.4" r="1.5" />
      <circle cx="18.5" cy="8.3" r="1.5" />
    </g>
    <path d="M4.5 4.2v2.6M3.2 5.5h2.6" stroke="hsl(var(--accent))" opacity="0.8" />
  </svg>
);

/** Measure MBLEx readiness — target + academic check */
export const ReadinessIcon = ({ className }: { className?: string }) => (
  <svg {...base} className={cn("h-5 w-5", className)}>
    <path d="M20.4 8.6a9 9 0 1 1-4.6-4.8" stroke="currentColor" />
    <circle cx="12" cy="12" r="4.6" stroke="currentColor" opacity="0.55" />
    <path d="m9.9 12.1 1.7 1.7 4.5-4.8" stroke="hsl(var(--accent))" />
    <path d="M18.3 3.2 20.8 4.4 18.3 5.6 15.8 4.4z" stroke="hsl(var(--accent))" opacity="0.9" />
  </svg>
);

/** Small academic mark used by the hero eyebrow */
export const SchoolsMarkIcon = ({ className }: { className?: string }) => (
  <svg {...base} className={cn("h-3.5 w-3.5", className)}>
    <path d="M12 4 21 8.2 12 12.4 3 8.2z" stroke="currentColor" />
    <path d="M6.6 10.4v4c0 1.4 2.4 2.6 5.4 2.6s5.4-1.2 5.4-2.6v-4" stroke="hsl(var(--accent))" />
  </svg>
);

/** Glass tile that hosts a hero icon */
export const IconTile = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => (
  <span
    className={cn(
      "schools-icon-tile relative flex h-10 w-10 shrink-0 items-center justify-center rounded-xl",
      "border border-primary/25 bg-card/50 text-primary backdrop-blur-sm",
      className,
    )}
  >
    <span className="pointer-events-none absolute inset-0 rounded-xl bg-gradient-to-br from-primary/15 via-transparent to-accent/10" />
    <span className="relative">{children}</span>
  </span>
);
